import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import api from '../services/api';
import { User } from '../types';

const ROLE_OPTIONS = [
  { value: 'Propietario', label: 'Propietario' },
  { value: 'Gerente', label: 'Gerente' },
  { value: 'Cajero', label: 'Cajero' },
  { value: 'EmpleadoPlus', label: 'Empleado plus (ve la caja)' },
  { value: 'Empleado', label: 'Empleado' },
  { value: 'Seller', label: 'Vendedor' },
  { value: 'Manager', label: 'Manager' },
  { value: 'Admin', label: 'Admin' },
  { value: 'Produccion', label: 'Producción' },
  { value: 'Calidad', label: 'Calidad' },
  { value: 'ReadOnly', label: 'Solo lectura' },
];

const emptyForm = { name: '', email: '', password: '', role: 'Seller' };

export const UsersPage: React.FC = () => {
  const { user } = useSelector((s: RootState) => s.auth);
  const isAdmin = useMemo(() => {
    const role = (user?.role || '').toLowerCase();
    return role === 'admin' || role === 'manager' || role === 'propietario' || role === 'gerente';
  }, [user?.role]);

  const [items, setItems] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<{ name: string; email: string; role: string; isActive: boolean; password: string }>({ name: '', email: '', role: 'Seller', isActive: true, password: '' });
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getUsers({ ...(roleFilter ? { role: roleFilter } : {}), includeInactive: showInactive });
      setItems(res.items || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudieron obtener los usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, roleFilter, showInactive]);

  const canCreate = form.name.trim().length > 2 && form.email.trim().length > 3 && form.password.length >= 6;

  const submit = async () => {
    if (!canCreate) return;
    setCreating(true);
    setError(null);
    setSuccess(null);
    try {
      await api.createUser({ ...form });
      setForm(emptyForm);
      setSuccess('Usuario creado correctamente');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al crear usuario');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (u: User) => {
    setEditing(u);
    setEditForm({ name: u.name, email: u.email, role: u.role, isActive: u.isActive, password: '' });
    setEditOpen(true);
    setSuccess(null);
    setError(null);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setEditSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.updateUser(editing.id, {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        isActive: editForm.isActive,
        password: editForm.password || undefined,
      });
      setEditOpen(false);
      setEditForm({ name: '', email: '', role: 'Seller', isActive: true, password: '' });
      await load();
      setSuccess('Usuario actualizado.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al actualizar usuario');
    } finally {
      setEditSaving(false);
    }
  };

  const toggleActive = async (u: User) => {
    try {
      await api.updateUser(u.id, { isActive: !u.isActive });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo actualizar el estado');
    }
  };

  const removeUser = async (u: User) => {
    if (!window.confirm(`¿Eliminar al usuario ${u.name}?`)) return;
    try {
      await api.deleteUser(u.id);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo eliminar el usuario');
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Usuarios</Typography>
        <Alert severity="info">Tu rol no tiene acceso a la gestión de usuarios.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }}>
        <Typography variant="h4" fontWeight={700}>Usuarios del tenant</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <FormControlLabel
            control={<Checkbox checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
            label="Mostrar eliminados/inactivos"
          />
          <TextField
            select
            size="small"
            label="Filtrar por rol"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            sx={{ width: { xs: '100%', md: 220 } }}
          >
            <MenuItem value="">Todos</MenuItem>
            {ROLE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </Stack>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Crear usuario</Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
              <TextField label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Contraseña temporal" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} helperText="Mínimo 6 caracteres. Compartila con el usuario en su primer ingreso." fullWidth />
              <TextField select label="Rol" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} fullWidth>
                {ROLE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={submit} disabled={!canCreate || creating}>
                {creating ? 'Creando...' : 'Crear usuario'}
              </Button>
              <Button variant="text" onClick={() => setForm(emptyForm)} disabled={creating}>Limpiar</Button>
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Usuarios registrados</Typography>
            <Button variant="outlined" onClick={load} disabled={loading}>
              {loading ? 'Actualizando...' : 'Actualizar'}
            </Button>
          </Stack>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Rol</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Último acceso</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{u.role}</TableCell>
                      <TableCell>
                        <Chip label={u.isActive ? 'Activo' : 'Inactivo'} color={u.isActive ? 'success' : 'default'} size="small" />
                      </TableCell>
                      <TableCell>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" variant="outlined" onClick={() => toggleActive(u)}>
                            {u.isActive ? 'Desactivar' : 'Activar'}
                          </Button>
                          <IconButton size="small" onClick={() => openEdit(u)}><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" disabled={u.id === user?.id} onClick={() => removeUser(u)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">Sin usuarios registrados con el filtro actual.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar usuario</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nombre" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            <TextField label="Email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            <TextField select label="Rol" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
              {ROLE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
            <TextField label="Contraseña (opcional)" type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} helperText="Completar sólo si querés reiniciar la contraseña" />
            <TextField select label="Estado" value={editForm.isActive ? 'Activo' : 'Inactivo'} onChange={(e) => setEditForm({ ...editForm, isActive: e.target.value === 'Activo' })}>
              <MenuItem value="Activo">Activo</MenuItem>
              <MenuItem value="Inactivo">Inactivo</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveEdit} disabled={editSaving}>{editSaving ? 'Guardando...' : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
